import React from 'react';
import ReactMarkdown from 'react-markdown';
import CollapsibleCode from './CollapsibleCode';

interface StreamingMarkdownProps {
  content: string;
  isStreaming: boolean;
}

const StreamingMarkdown: React.FC<StreamingMarkdownProps> = ({ content, isStreaming }) => {
  if (!content) return null;

  // During streaming, we need to handle incomplete markdown gracefully
  let processedContent = content;
  
  if (isStreaming) {
    // Fix unclosed code blocks
    const codeBlockMatches = content.match(/```/g) || [];
    if (codeBlockMatches.length % 2 !== 0) {
      // Odd number means unclosed - add closing
      processedContent += '\n```';
    }
    
    // Fix unclosed emphasis/bold markers
    // Count unescaped asterisks
    const unescapedAsterisks = content.replace(/\\\*/g, '').match(/\*/g) || [];
    if (unescapedAsterisks.length % 2 !== 0) {
      // Check if it's likely a bold marker
      const lastAsteriskIndex = content.lastIndexOf('*');
      if (lastAsteriskIndex > 0 && content[lastAsteriskIndex - 1] === '*') {
        processedContent += '*'; // Close bold
      } else {
        processedContent += '*'; // Close emphasis
      }
    }
    
    // Fix unclosed underscores
    const unescapedUnderscores = content.replace(/\\_/g, '').match(/_/g) || [];
    if (unescapedUnderscores.length % 2 !== 0) {
      processedContent += '_';
    }
    
    // Fix unclosed links/images
    const openBrackets = (content.match(/\[/g) || []).length;
    const closeBrackets = (content.match(/\]/g) || []).length;
    if (openBrackets > closeBrackets) {
      // Likely an unclosed link - close it
      processedContent += '](#)';
    }
  }

  return (
    <ReactMarkdown
      components={{
        h1: ({ children }) => <h1 style={{ fontWeight: 600, fontSize: '20px', margin: '16px 0 8px 0' }}>{children}</h1>,
        h2: ({ children }) => <h2 style={{ fontWeight: 600, fontSize: '18px', margin: '14px 0 6px 0' }}>{children}</h2>,
        h3: ({ children }) => <h3 style={{ fontWeight: 600, fontSize: '16px', margin: '12px 0 4px 0' }}>{children}</h3>,
        h4: ({ children }) => <h4 style={{ fontWeight: 600, fontSize: '15px', margin: '10px 0 4px 0' }}>{children}</h4>,
        h5: ({ children }) => <h5 style={{ fontWeight: 600, fontSize: '14px', margin: '8px 0 4px 0' }}>{children}</h5>,
        h6: ({ children }) => <h6 style={{ fontWeight: 600, fontSize: '14px', margin: '8px 0 4px 0' }}>{children}</h6>,
        p: ({ children }) => <p style={{ margin: '8px 0', lineHeight: '1.6' }}>{children}</p>,
        ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
        ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
        li: ({ children }) => <li style={{ margin: '4px 0' }}>{children}</li>,
        blockquote: ({ children }) => (
          <blockquote style={{ 
            borderLeft: '3px solid #e5e7eb', 
            paddingLeft: '12px', 
            margin: '8px 0',
            fontStyle: 'italic',
            color: '#6b7280'
          }}>
            {children}
          </blockquote>
        ),
        code: ({ inline, className, children, ...props }: any) => {
          const match = /language-(\w+)/.exec(className || '');
          const language = match ? match[1] : '';
          
          if (!inline && language === 'python') {
            const codeContent = String(children).replace(/\n$/, '');
            return <CollapsibleCode code={codeContent} language="python" />;
          }
          
          if (inline) {
            return (
              <code 
                style={{
                  backgroundColor: '#f3f4f6',
                  padding: '2px 4px',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                  fontFamily: 'Monaco, Menlo, Consolas, monospace'
                }}
                {...props}
              >
                {children}
              </code>
            );
          }
          
          return (
            <pre style={{
              backgroundColor: '#f9fafb',
              padding: '12px',
              borderRadius: '6px',
              overflow: 'auto',
              margin: '8px 0'
            }}>
              <code className={className} {...props}>
                {children}
              </code>
            </pre>
          );
        },
        strong: ({ children }) => <strong style={{ fontWeight: 600 }}>{children}</strong>,
        em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
        hr: () => <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />,
        a: ({ href, children }) => (
          <a 
            href={href} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#FF6600', textDecoration: 'none' }}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            {children}
          </a>
        ),
      }}
    >
      {processedContent}
    </ReactMarkdown>
  );
};

export default StreamingMarkdown;